<template>
  <div class="full-width cursor-pointer p-calendar">
    <date-picker
      :ref="props.name"
      :model-value="dateValue"
      v-bind="datePickerAttributes"
      :popover="{ placement: direction }"
      @popover-did-show="() => (isOpen = true)"
      @popover-did-hide="() => (isOpen = false)"
      @dayclick="(day) => onDayClick(day)"
    >
      <template #default="{ togglePopover }">
        <div :id="props.name" :name="props.name" :class="calendarAttributes" @click="togglePopover">
          <p class="f-utility-14 t-fg-secondary" v-text="props.title" />
          <p v-if="dateValue" class="f-utility-14 t-fg-primary" v-text="displayDate" />
        </div>
      </template>
    </date-picker>
    <p-error-message v-if="errorState" :name="name" class="p-text-input--error" />
  </div>
</template>

<script lang="ts" setup>
import { type Placement } from '@popperjs/core'
import type { DayData } from 'v-calendar/dist/types/tests/unit/util/dayData.js'
import { useField, ErrorMessage as PErrorMessage } from 'vee-validate'
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'

const emit = defineEmits(['day-click'])

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  date: {
    type: Date || String,
    default: () => new Date(),
  },
  title: {
    type: String,
    default: '',
  },
  minDate: {
    type: String,
    default: '',
  },
  maxDate: {
    type: String,
    default: '',
  },
  direction: {
    type: String as () => Placement,
    default: 'bottom-start',
  },
  offset: {
    type: String,
    default: '',
  },
  error: {
    type: Boolean,
    default: false,
  },
})

const colorMode = useColorMode()

const isOpen = ref(false)

const dateValue = computed(() => props.date)

const calendarAttributes = computed(() => {
  return [
    'p-calendar__regular place-items-start align-items-start px-12 py-6 flex-column br-6 full-width ',
    isOpen.value ? 'p-calendar__border-active' : 'p-calendar__border-inactive',
    errorState.value ? 'p-calendar__error f-utility-14' : 'place-items-center',
  ]
})

const displayDate = computed(() => {
  return props.date.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })
})

const datePickerAttributes = computed((): Record<string, any> => {
  const attributes = {
    color: 'green',
    locale: 'tr',
    isDark: colorMode.preference === 'dark',
    key: 'today',
    highlight: 'green',
    dates: new Date(),
  } as any

  if (props.minDate) attributes.minDate = new Date(props.minDate)

  if (props.maxDate) attributes.maxDate = new Date(props.maxDate)

  return attributes
})

const errorState = computed<boolean>(() => {
  return props.error || (!meta.valid && meta.validated)
})

const { value, meta } = useField(props.name, undefined, {
  initialValue: dateValue.value,
})

watch(
  () => dateValue.value,
  () => (value.value = dateValue.value || ''),
)

const onDayClick = (day: DayData) => {
  emit('day-click', day)
}
</script>
